import { useState, useRef, useEffect } from "react";
import { Phone, Mail, Calendar, MessageSquare, MoreVertical, Clock, MessagesSquare, Plus } from "lucide-react";

import ClubChip from "./ClubChip";
import StatusPill from "./StatusPill";
import { modeLabel, entryBoardStatus, STATUS } from "../../../constants/taskboardStatus";
import { entryAgeDays } from "../../../utils/taskboardActivity";

const MODE_ICON = {
  phone: Phone,
  email: Mail,
  meeting: Calendar,
};

function initials(name) {
  if (!name) return "?";
  return name.split(" ").filter(Boolean).map((w) => w[0]).join("").toUpperCase().slice(0, 2);
}

// One deal-tracker entry card, shared by board columns and timeline.
// Presentational + a few action callbacks; parent owns persistence.
const EntryCard = ({
  entry,
  onStatusChange,
  onEdit,
  onDelete,
  onOpenThread,
  canModify = false,
  dimmed = false,
  showStatusPill = true,
  dragHandleProps,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const Icon = MODE_ICON[entry.mode_of_communication] || MessageSquare;
  const updates = entry.updates || [];
  const updateCount = updates.length;
  const isWaiting = entryBoardStatus(entry) === STATUS.WAITING;
  const age = isWaiting ? entryAgeDays(entry) : null;
  const author = entry.agent?.name || "";
  // Status changes are entry PATCHes the server only allows for the entry's
  // agent or an agency admin — same canModify as the Edit/Delete menu, so the
  // pill and the drag handle must not offer the action to anyone else.
  const draggable = canModify && dragHandleProps;
  const readOnlyHint = "Only the entry's agent or an agency admin can change this entry.";

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => menuRef.current && !menuRef.current.contains(e.target) && setMenuOpen(false);
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  return (
    <div
      className={`bg-white border rounded-[10px] shadow-sm p-3 flex flex-col gap-2.5 ${dimmed ? "opacity-[.85]" : ""}`}
      style={{ borderColor: isWaiting ? "#F4D8A6" : "#E4E9F0" }}
    >
      {/* Header: type + overflow */}
      <div className="flex items-center gap-2">
        <span
          className={`w-6 h-6 rounded-[7px] bg-[#F2F4F7] flex items-center justify-center flex-none text-[#475467] ${draggable ? "cursor-grab" : ""}`}
          {...(dragHandleProps || {})}
          title={draggable ? "Drag to change status" : undefined}
        >
          <Icon className="w-3.5 h-3.5" />
        </span>
        <span className="text-[13px] font-semibold text-[#101828]">
          {modeLabel(entry.mode_of_communication)}
        </span>
        <div className="ml-auto flex items-center gap-1.5">
          {showStatusPill && (
            <StatusPill
              status={entryBoardStatus(entry)}
              readOnly={!canModify}
              title={!canModify ? readOnlyHint : undefined}
              onChange={(s) => onStatusChange && onStatusChange(entry, s)}
            />
          )}
          {canModify && (
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenuOpen((o) => !o)}
                className="w-6 h-6 flex items-center justify-center rounded hover:bg-[#F2F4F7] text-[#98A2B3]"
                aria-label="Entry actions"
              >
                <MoreVertical className="w-4 h-4" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-full mt-1 z-30 w-44 bg-white border border-[#E4E9F0] rounded-lg shadow-lg py-1">
                  <button onClick={() => { setMenuOpen(false); onOpenThread && onOpenThread(entry); }} className="block w-full text-left px-3 py-2 text-[13px] font-medium text-[#1866CC] hover:bg-[#F8FAFC]">
                    Add / view notes
                  </button>
                  <button onClick={() => { setMenuOpen(false); onEdit && onEdit(entry); }} className="block w-full text-left px-3 py-2 text-[13px] text-[#344054] hover:bg-[#F8FAFC]">
                    Edit entry
                  </button>
                  <button onClick={() => { setMenuOpen(false); onDelete && onDelete(entry); }} className="block w-full text-left px-3 py-2 text-[13px] text-[#B91C1C] hover:bg-red-50">
                    Delete entry
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Note (the original, never mutated by updates) */}
      <button
        type="button"
        onClick={() => onOpenThread && onOpenThread(entry)}
        className="text-left text-[13px] leading-[1.45] text-[#475467] hover:text-[#101828]"
      >
        {entry.notes}
      </button>

      <ClubChip entry={entry} />

      {/* Footer */}
      <div className="flex items-center gap-1.5 border-t border-[#F2F4F7] pt-2">
        <span className="text-[12px] text-[#98A2B3]">
          {entry.date}
        </span>
        {updateCount > 0 ? (
          <button
            type="button"
            onClick={() => onOpenThread && onOpenThread(entry)}
            className="inline-flex items-center gap-1 bg-[#EAF2FF] text-[#1866CC] text-[11px] font-semibold px-1.5 py-px rounded-full hover:brightness-95"
            title="Add a note / view updates"
          >
            <MessagesSquare className="w-3 h-3" />
            {updateCount} {updateCount === 1 ? "update" : "updates"}
          </button>
        ) : (
          // Always-visible entry point to add a timestamped note, even when
          // there are no updates yet (the update thread was hard to find).
          <button
            type="button"
            onClick={() => onOpenThread && onOpenThread(entry)}
            className="inline-flex items-center gap-1 text-[#1866CC] text-[11px] font-semibold px-1.5 py-px rounded-full hover:bg-[#EAF2FF]"
            title="Add a timestamped note to this entry"
          >
            <Plus className="w-3 h-3" />
            Add note
          </button>
        )}
        {age != null && age >= 1 && (
          <span className="inline-flex items-center gap-1 bg-[#FFF8EB] text-[#B54708] text-[11px] font-semibold px-1.5 py-px rounded-full" title="Days waiting">
            <Clock className="w-3 h-3" />
            {age}d
          </span>
        )}
        {author && (
          <span
            className="ml-auto w-5 h-5 rounded-full bg-[#FDEAD7] text-[#B93815] flex items-center justify-center text-[8px] font-bold flex-none"
            title={author}
          >
            {initials(author)}
          </span>
        )}
      </div>
    </div>
  );
};

export default EntryCard;
